<template>
  <div>
    <v-container fluid>
      <!-- Header -->

      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{
            "Validation Master Data"
          }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="pa-4" elevation="2">
        <!-- Search and Filter Row -->
        <v-row class="mb-4" align="center">
          <v-col cols="12" md="3">
            <v-select v-model="filters.category" :items="categoryOptions" item-title="text" item-value="value"
              label="Category" variant="outlined" density="compact" clearable hide-details
              @update:modelValue="fetchData"></v-select>
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="filters.isActive" :items="statusOptions" item-title="text" item-value="value"
              label="Status" variant="outlined" density="compact" clearable hide-details
              @update:modelValue="fetchData"></v-select>
          </v-col>
          <!-- <v-col cols="12" md="4">
            <v-text-field v-model="filters.search" label="Search (Question ID, Question, Tags)" variant="outlined"
              density="compact" hide-details clearable prepend-inner-icon="mdi-magnify" @keyup.enter="fetchData"
              @click:clear="filters.search = ''; fetchData()"></v-text-field>
          </v-col> -->
          <v-col cols="12" md="6" class="text-right">
            <v-btn color="primary" variant="elevated" @click="openAddDialog" prepend-icon="mdi-plus">
              Add Question
            </v-btn>
            <v-btn color="secondary" variant="outlined" class="ml-2" @click="openCategoryDialog"
              prepend-icon="mdi-folder-cog">
              Categories
            </v-btn>
          </v-col>
        </v-row>

        <!-- Category Stats -->
        <v-row class="mb-4" v-if="categoryStats.length > 0">
          <v-col cols="12">
            <div class="d-flex flex-wrap ga-2">
              <v-chip v-for="stat in categoryStats" :key="stat.category" :color="stat.color" variant="tonal"
                size="small">
                {{ stat.category }} ({{ stat.count }})
              </v-chip>
            </div>
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table-server v-model:items-per-page="itemsPerPage" :headers="headers" :items="items"
          :items-length="totalItems" :loading="loading" :items-per-page-options="[10, 25, 50, 100]" class="elevation-0"
          @update:options="onOptionsUpdate">
          <template v-slot:item.number="{ item }">
            <span class="text-body-2">{{ item.number }}</span>
          </template>

          <template v-slot:item.question="{ item }">
            <span class="text-body-2" :title="item.question">
              {{ truncateText(item.question, 60) }}
            </span>
          </template>

          <template v-slot:item.category="{ item }">
            <v-chip size="small" :color="getCategoryColor(item.category)" variant="tonal">
              {{ item.category }}
            </v-chip>
          </template>

          <template v-slot:item.answerType="{ item }">
            <v-chip size="small" :color="item.answerType === 'exact' ? 'success' : 'info'" variant="tonal">
              {{ getAnswerTypeText(item.answerType) }}
            </v-chip>
          </template>

          <template v-slot:item.questionType="{ item }">
            <v-chip v-if="item.questionType" size="small" color="secondary" variant="outlined">
              {{ item.questionType || '-' }}
            </v-chip>
            <span v-else class="text-grey-lighten-1">-</span>
          </template>

          <template v-slot:item.expectedIntentFull="{ item }">
            <span class="text-body-2 text-grey-darken-1" :title="item.expectedIntentFull">
              {{ truncateText(item.expectedIntentFull, 40) }}
            </span>
          </template>

          <template v-slot:item.isActive="{ item }">
            <v-chip :color="item.isActive ? 'success' : 'grey'" size="small" variant="flat">
              {{ item.isActive ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn icon variant="text" size="small" color="primary" @click="openEditDialog(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon variant="text" size="small" color="error" @click="confirmDelete(item)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
        </v-data-table-server>
      </v-card>
    </v-container>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="formDialog" max-width="900px" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4 bg-primary">
          <v-icon class="mr-2">{{ isEditMode ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
          {{ isEditMode ? 'Edit Question' : 'Add New Question' }}
        </v-card-title>

        <v-card-text class="pa-4">
          <v-form ref="formRef" v-model="formValid">
            <!-- ROW 1: Question (Full Width) -->
            <div class="text-subtitle-2 mb-2 mt-2 text-grey-darken-1">
              Question *
            </div>
            <v-row>
              <v-col cols="12">
                <v-textarea v-model="form.Question" variant="outlined" density="compact" rows="2"
                  :rules="[rules.required]" placeholder="Enter the test question" hide-details></v-textarea>
              </v-col>
            </v-row>

            <!-- ROW 2: Answer Guide (Full Width) -->
            <div class="text-subtitle-2 mb-2 mt-3 text-grey-darken-1">
              Answer Guide
              <v-tooltip location="top" max-width="300">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props" size="x-small" class="ml-1 cursor-pointer"
                    color="grey-darken-1">mdi-information-outline</v-icon>
                </template>
                <div>
                  Additional context to help AI judge answer quality.
                  Specify key points or keywords to look for.
                </div>
              </v-tooltip>
            </div>
            <v-row>
              <v-col cols="12">
                <v-textarea v-model="form.AnswerGuide" variant="outlined" density="compact" rows="3"
                  placeholder="Optional: Additional context to help AI judge answer quality" hide-details></v-textarea>
              </v-col>
            </v-row>

            <!-- ROW 3: Category | Question Type -->
            <v-row class="mt-3">
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-2 text-grey-darken-1">
                  Category
                </div>
                <v-select v-model="form.Category" :items="categories" item-title="categoryName"
                  item-value="categoryName" variant="outlined" density="compact" clearable
                  hide-details></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-2 text-grey-darken-1">
                  Question Type
                </div>
                <v-text-field v-model="form.QuestionType" variant="outlined" density="compact"
                  placeholder="e.g., FAQ, Product, Service" hide-details></v-text-field>
              </v-col>
            </v-row>

            <!-- ROW 4: Answer Type | Validation Selection -->
            <v-row class="mt-3">
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-2 text-grey-darken-1">
                  {{ answerTypeLabel }}
                  <v-tooltip location="top" max-width="350">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="ml-1 cursor-pointer" color="grey-darken-1">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div>
                      <strong>Guideline:</strong> Bot should cover main points.
                      AI judge checks if answer addresses the topic adequately.<br />
                      <strong>Exact:</strong> Bot must provide specific facts (numbers, dates, names).
                      AI judge checks if key information matches.
                    </div>
                  </v-tooltip>
                </div>
                <v-radio-group v-model="form.AnswerType" inline hide-details color="#4CAF50"
                  @update:modelValue="onAnswerTypeChange">
                  <v-radio label="Guideline" value="guide" />
                  <v-radio label="Exact" value="exact" />
                </v-radio-group>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-subtitle-2 mb-2 text-grey-darken-1">
                  Validations
                  <v-tooltip location="top" max-width="350">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="x-small" class="ml-1 cursor-pointer" color="grey-darken-1">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <div>
                      <strong>Answer Quality:</strong> Always validates if the chatbot gives the correct answer<br />
                      <strong>Advanced Router (Optional):</strong> Additionally validates intent routing accuracy to help debug WHERE the routing script might fail.
                    </div>
                  </v-tooltip>
                </div>
                <div class="d-flex align-center">
                  <v-checkbox v-model="form.ValidateAnswer" label="Answer Quality*" color="success"
                    hide-details density="compact" disabled readonly></v-checkbox>
                  <v-checkbox v-model="form.ValidateIntent" label="Advanced Router" color="primary"
                    hide-details density="compact" class="ml-4"></v-checkbox>
                </div>
              </v-col>
            </v-row>

            <!-- Expected Intent Levels Section (Conditional - Only show when ValidateIntent is checked) -->
            <template v-if="form.ValidateIntent">
              <v-divider class="my-4"></v-divider>

              <!-- Intent Level Selector -->
              <div class="text-subtitle-2 mb-3 text-grey-darken-1 d-flex align-center">
                <v-icon class="mr-2" size="small">mdi-sitemap</v-icon>
                Expected Intent Levels *
                <v-chip size="x-small" color="primary" variant="tonal" class="ml-2">
                  Required for Intent Validation
                </v-chip>
              </div>

              <!-- Level Count Selection -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-chip-group v-model="form.levelCount" mandatory selected-class="text-primary"
                    @update:modelValue="handleLevelCountChange">
                    <v-chip value="1">1 Level</v-chip>
                    <v-chip value="2">2 Levels</v-chip>
                    <v-chip value="3">3 Levels</v-chip>
                    <v-chip value="4">4 Levels</v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>

              <!-- Dynamic Dropdown Fields -->
              <v-row v-if="form.levelCount >= 1">
                <v-col v-if="form.levelCount >= 1" cols="12">
                  <v-select v-model="form.ExpectedIntentL1" :items="filteredLevel1Options" item-title="title"
                    item-value="value" label="Level 1 (L1) *" variant="outlined" density="compact"
                    :rules="form.ValidateIntent ? [rules.required] : []" @update:modelValue="handleL1Change">
                    <template v-slot:prepend-inner>
                      <v-icon size="small" color="primary">mdi-numeric-1-box</v-icon>
                    </template>
                  </v-select>
                </v-col>
                <v-col v-if="form.levelCount >= 2" cols="12">
                  <v-select v-model="form.ExpectedIntentL2" :items="filteredLevel2Options" item-title="title"
                    item-value="value" label="Level 2 (L2) *" variant="outlined" density="compact"
                    :disabled="!form.ExpectedIntentL1"
                    :rules="form.ValidateIntent && form.levelCount >= 2 ? [rules.required] : []"
                    @update:modelValue="handleL2Change">
                    <template v-slot:prepend-inner>
                      <v-icon size="small" color="primary">mdi-numeric-2-box</v-icon>
                    </template>
                  </v-select>
                </v-col>
                <v-col v-if="form.levelCount >= 3" cols="12">
                  <v-select v-model="form.ExpectedIntentL3" :items="filteredLevel3Options" item-title="title"
                    item-value="value" label="Level 3 (L3) *" variant="outlined" density="compact"
                    :disabled="!form.ExpectedIntentL2"
                    :rules="form.ValidateIntent && form.levelCount >= 3 ? [rules.required] : []"
                    @update:modelValue="handleL3Change">
                    <template v-slot:prepend-inner>
                      <v-icon size="small" color="primary">mdi-numeric-3-box</v-icon>
                    </template>
                  </v-select>
                </v-col>
                <v-col v-if="form.levelCount >= 4" cols="12">
                  <v-select v-model="form.ExpectedIntentL4" :items="filteredLevel4Options" item-title="title"
                    item-value="value" label="Level 4 (L4) *" variant="outlined" density="compact"
                    :disabled="!form.ExpectedIntentL3"
                    :rules="form.ValidateIntent && form.levelCount >= 4 ? [rules.required] : []"
                    @update:modelValue="updateFullPath">
                    <template v-slot:prepend-inner>
                      <v-icon size="small" color="primary">mdi-numeric-4-box</v-icon>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <!-- Full Intent Path - Auto-generated, Read-only -->
              <v-row>
                <v-col cols="12">
                  <v-text-field :model-value="fullIntentPath" label="Full Intent Path" variant="outlined"
                    density="compact" readonly class="intent-path-field" disabled>
                    <template v-slot:prepend-inner>
                      <v-icon size="small" color="success">mdi-link-variant</v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </template>
          </v-form>
        </v-card-text>

        <v-divider class="border-opacity-100" color="grey-lighten-2" thickness="2"></v-divider>

        <v-card-actions class="px-4 py-1">
          <div class="d-flex align-center">
            <span class="text-subtitle-2 text-grey-darken-1 mr-3">Status:</span>
            <v-switch
              v-model="form.IsActive"
              color="success"
              hide-details
              density="comfortable"
              inset
              class="custom-switch">
              <template v-slot:label>
                <span :class="form.IsActive ? 'text-success font-weight-medium' : 'text-grey'">
                  {{ form.IsActive ? 'Active' : 'Inactive' }}
                </span>
              </template>
            </v-switch>
          </div>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="closeFormDialog">Cancel</v-btn>
          <v-btn color="primary" variant="elevated" :loading="saving" :disabled="!formValid" @click="saveForm">
            {{ isEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Category Management Dialog -->
    <v-dialog v-model="categoryDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="text-h6 pa-4 bg-secondary">
          <v-icon class="mr-2">mdi-folder-cog</v-icon>
          Category Management
        </v-card-title>

        <v-card-text class="pa-4">
          <v-row class="mb-4">
            <v-col cols="12" class="text-right">
              <v-btn color="primary" size="small" variant="elevated" @click="openAddCategoryForm"
                prepend-icon="mdi-plus">
                Add Category
              </v-btn>
            </v-col>
          </v-row>

          <!-- Category List -->
          <v-table density="compact">
            <thead>
              <tr>
                <th>Name</th>
                <th>Color</th>
                <th>Questions</th>
                <th>Status</th>
                <th class="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cat in categories" :key="cat.id">
                <td>{{ cat.categoryName }}</td>
                <td>
                  <v-chip :color="cat.color" size="x-small" variant="flat">
                    {{ cat.color || 'Default' }}
                  </v-chip>
                </td>
                <td>{{ cat.questionCount }}</td>
                <td>
                  <v-chip :color="cat.isActive ? 'success' : 'grey'" size="x-small" variant="flat">
                    {{ cat.isActive ? 'Active' : 'Inactive' }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-btn icon variant="text" size="x-small" @click="editCategory(cat)">
                    <v-icon size="small">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon variant="text" size="x-small" color="error" @click="confirmDeleteCategory(cat)"
                    :disabled="cat.questionCount > 0">
                    <v-icon size="small">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>

          <!-- Category Form -->
          <v-expand-transition>
            <div v-if="showCategoryForm" class="mt-4 pa-4 bg-grey-lighten-4 rounded">
              <div class="text-subtitle-2 mb-3">
                {{ isEditCategoryMode ? 'Edit Category' : 'New Category' }}
              </div>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field v-model="categoryForm.categoryName" label="Name *" variant="outlined" density="compact"
                    :rules="[rules.required]"></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field v-model="categoryForm.color" label="Color (Hex)" variant="outlined" density="compact"
                    placeholder="#FF5733">
                    <template v-slot:prepend-inner>
                      <div
                        :style="{ backgroundColor: categoryForm.color || '#607D8B', width: '20px', height: '20px', borderRadius: '4px' }">
                      </div>
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field v-model.number="categoryForm.sortOrder" label="Sort Order" variant="outlined"
                    density="compact" type="number"></v-text-field>
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-switch v-model="categoryForm.isActive" label="Active" color="success" hide-details
                    density="compact"></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="categoryForm.description" label="Description" variant="outlined"
                    density="compact"></v-text-field>
                </v-col>
              </v-row>
              <div class="text-right mt-2">
                <v-btn variant="text" size="small" @click="cancelCategoryForm">Cancel</v-btn>
                <v-btn color="primary" variant="elevated" size="small" class="ml-2" :loading="savingCategory"
                  @click="saveCategory">
                  {{ isEditCategoryMode ? 'Update' : 'Create' }}
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="categoryDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
export default {
  name: 'ValidationMasterDataV2',
  inject: ["flagNav","FontColorTitle","SubNavbarBackGround"],
  data() {
    return {
      today: null,
      showGuidelineAnswer: false,
      showExpectedAnswer: false,
      // Table data
      items: [],
      totalItems: 0,
      itemsPerPage: 10,
      loading: false,
      options: {},

      // Filters
      filters: {
        category: null,
        search: '',
        isActive: null,
      },

      // Categories
      categories: [],
      categoryStats: [],

      // Form dialog
      formDialog: false,
      formValid: false,
      isEditMode: false,
      saving: false,
      form: {
        ID: null,
        Question: '',
        AnswerType: 'guide', // Default to guide
        QuestionType: '',    // Question type classification
        ExpectedAnswer: '',
        Category: '',
        ValidateAnswer: true, // V2: Flag for answer validation (default checked)
        ValidateIntent: false, // V2: Flag for intent validation
        levelCount: '4', // Default to 4 levels
        ExpectedIntentL1: '',
        ExpectedIntentL2: '',
        ExpectedIntentL3: '',
        ExpectedIntentL4: '',
        ExpectedIntentFull: '',
        AnswerGuide: '',
        Tags: '',
        IsActive: true,
      },

      // Intent Hierarchy Data (from IntentRoutingTree API)
      level1Options: [],
      level2Options: [],
      level3Options: [],
      level4Options: [],
      // Selected parent IDs for cascading
      selectedL1Id: null,
      selectedL2Id: null,
      selectedL3Id: null,

      // Category dialog
      categoryDialog: false,
      showCategoryForm: false,
      isEditCategoryMode: false,
      savingCategory: false,
      categoryForm: {
        id: null,
        categoryName: '',
        description: '',
        color: '#607D8B',
        sortOrder: 0,
        isActive: true,
      },

      // Table headers (use camelCase to match API response)
      headers: [
        { title: '#', key: 'number', width: '60px', sortable: false },
        { title: 'Category', key: 'category', width: '140px' },
        { title: 'Question', key: 'question' },
        { title: 'Answer Type', key: 'answerType', width: '180px' },
        { title: 'Question Type', key: 'questionType', width: '180px' },
        { title: 'Expected Intent', key: 'expectedIntentFull', width: '280px' },
        { title: 'Status', key: 'isActive', width: '100px', align: 'center' },
        { title: 'Actions', key: 'actions', width: '120px', sortable: false, align: 'center' },
      ],

      // Filter options
      statusOptions: [
        { text: 'All', value: null },
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],

      // Answer type options
      answerTypeOptions: [
        { text: 'Exact', value: 'exact' },
        { text: 'Guideline', value: 'guide' },
      ],

      // Question type options
      questionTypeOptions: [
        { text: 'FAQ', value: 'faq' },
        { text: 'Product', value: 'product' },
        { text: 'Service', value: 'service' },
        { text: 'Transaction', value: 'transaction' },
        { text: 'General', value: 'general' },
      ],

      // Validation rules
      rules: {
        required: (v) => !!v || 'This field is required',
        requiredIfNotEmpty: (v) => {
          // Only validate if value exists and is not empty
          if (v === null || v === undefined || v === '') return true;
          return !!v || 'This field is required';
        },
      },
    };
  },

  computed: {
    // Dynamic Answer Type Label
    answerTypeLabel() {
      const typeText = this.form.AnswerType === 'guide' ? 'Guideline' : 'Exact';
      return `Answer Type (${typeText})`;
    },

    currentDate() {
      return new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    categoryOptions() {
      const options = [{ text: 'All Categories', value: null }];
      this.categories.forEach((cat) => {
        options.push({
          text: `${cat.categoryName}`, //(${cat.questionCount})
          value: cat.categoryName,
        });
      });
      return options;
    },

    // Filtered Level Options (populated from API based on parent selection)
    filteredLevel1Options() {
      return this.level1Options.map(x => ({ title: x.intentName, value: x.intentName, id: x.intentID }));
    },

    filteredLevel2Options() {
      return this.level2Options.map(x => ({ title: x.intentName, value: x.intentName, id: x.intentID }));
    },

    filteredLevel3Options() {
      return this.level3Options.map(x => ({ title: x.intentName, value: x.intentName, id: x.intentID }));
    },

    filteredLevel4Options() {
      return this.level4Options.map(x => ({ title: x.intentName, value: x.intentName, id: x.intentID }));
    },

    // Auto-generate Full Intent Path
    fullIntentPath() {
      const levels = [];
      const levelCount = parseInt(this.form.levelCount) || 4;

      if (levelCount >= 1 && this.form.ExpectedIntentL1) {
        levels.push(this.form.ExpectedIntentL1);
      }
      if (levelCount >= 2 && this.form.ExpectedIntentL2) {
        levels.push(this.form.ExpectedIntentL2);
      }
      if (levelCount >= 3 && this.form.ExpectedIntentL3) {
        levels.push(this.form.ExpectedIntentL3);
      }
      if (levelCount >= 4 && this.form.ExpectedIntentL4) {
        levels.push(this.form.ExpectedIntentL4);
      }

      return levels.join(' > ');
    },
  },

  mounted() {
    this.GetDate();
    this.fetchCategories();
    this.fetchCategoryStats();
    this.fetchIntentLevel1();
  },

  methods: {
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
          dd = "0" + dd;
      }
      if (mm < 10) {
          mm = "0" + mm;
      }

      this.today = dd + "/" + mm + "/" + yyyy;
    },
    onAnswerTypeChange(value) {
      if (value === "guide") {
        this.showGuidelineAnswer = true
      }else{
        this.showGuidelineAnswer = false
      }

      if (value === "exact") {
        this.showExpectedAnswer = true
      }else{
        this.showExpectedAnswer = false
      }
    },
    // Fetch Intent Level 1 (top-level intents)
    fetchIntentLevel1() {
      let self = this;
      self.$call.GetValidationIntentLevel1(
        (response) => {
          if (response.status === 0) {
            self.level1Options = response.data || [];
          } else {
            self.level1Options = [];
            console.warn('Failed to load Level 1 intents:', response.message);
          }
        },
        (error) => {
          self.level1Options = [];
          console.error('Error loading Level 1 intents:', error);
        }
      );
    },

    // Fetch Intent Level 2 by parent ID
    fetchIntentLevel2(parentId) {
      if (!parentId) {
        this.level2Options = [];
        return;
      }
      let self = this;
      self.$call.GetValidationIntentLevel2(
        parentId,
        (response) => {
          if (response.status === 0) {
            self.level2Options = response.data || [];
          } else {
            self.level2Options = [];
            console.warn('Failed to load Level 2 intents:', response.message);
          }
        },
        (error) => {
          self.level2Options = [];
          console.error('Error loading Level 2 intents:', error);
        }
      );
    },

    // Fetch Intent Level 3 by parent ID
    fetchIntentLevel3(parentId) {
      if (!parentId) {
        this.level3Options = [];
        return;
      }
      let self = this;
      self.$call.GetValidationIntentLevel3(
        parentId,
        (response) => {
          if (response.status === 0) {
            self.level3Options = response.data || [];
          } else {
            self.level3Options = [];
            console.warn('Failed to load Level 3 intents:', response.message);
          }
        },
        (error) => {
          self.level3Options = [];
          console.error('Error loading Level 3 intents:', error);
        }
      );
    },

    // Fetch Intent Level 4 by parent ID
    fetchIntentLevel4(parentId) {
      if (!parentId) {
        this.level4Options = [];
        return;
      }
      let self = this;
      self.$call.GetValidationIntentLevel4(
        parentId,
        (response) => {
          if (response.status === 0) {
            self.level4Options = response.data || [];
          } else {
            self.level4Options = [];
            console.warn('Failed to load Level 4 intents:', response.message);
          }
        },
        (error) => {
          self.level4Options = [];
          console.error('Error loading Level 4 intents:', error);
        }
      );
    },

    // Intent Level Handlers
    handleLevelCountChange(newCount) {
      // Clear levels that are no longer needed
      const count = parseInt(newCount);
      if (count < 4) this.form.ExpectedIntentL4 = '';
      if (count < 3) this.form.ExpectedIntentL3 = '';
      if (count < 2) this.form.ExpectedIntentL2 = '';
      this.updateFullPath();
    },

    handleL1Change(value) {
      // Clear child levels when parent changes
      this.form.ExpectedIntentL2 = '';
      this.form.ExpectedIntentL3 = '';
      this.form.ExpectedIntentL4 = '';
      this.level2Options = [];
      this.level3Options = [];
      this.level4Options = [];
      this.selectedL1Id = null;
      this.selectedL2Id = null;
      this.selectedL3Id = null;

      // Find the selected L1 item to get its ID
      if (value) {
        const selected = this.level1Options.find(x => x.intentName === value);
        if (selected) {
          this.selectedL1Id = selected.intentID;
          this.fetchIntentLevel2(selected.intentID);
        }
      }
      this.updateFullPath();
    },

    handleL2Change(value) {
      // Clear child levels when parent changes
      this.form.ExpectedIntentL3 = '';
      this.form.ExpectedIntentL4 = '';
      this.level3Options = [];
      this.level4Options = [];
      this.selectedL2Id = null;
      this.selectedL3Id = null;

      // Find the selected L2 item to get its ID
      if (value) {
        const selected = this.level2Options.find(x => x.intentName === value);
        if (selected) {
          this.selectedL2Id = selected.intentID;
          this.fetchIntentLevel3(selected.intentID);
        }
      }
      this.updateFullPath();
    },

    handleL3Change(value) {
      // Clear child levels when parent changes
      this.form.ExpectedIntentL4 = '';
      this.level4Options = [];
      this.selectedL3Id = null;

      // Find the selected L3 item to get its ID
      if (value) {
        const selected = this.level3Options.find(x => x.intentName === value);
        if (selected) {
          this.selectedL3Id = selected.intentID;
          this.fetchIntentLevel4(selected.intentID);
        }
      }
      this.updateFullPath();
    },

    updateFullPath() {
      // Sync the fullIntentPath to form.ExpectedIntentFull
      this.form.ExpectedIntentFull = this.fullIntentPath;
    },

    // Data fetching
    onOptionsUpdate(options) {
      this.options = options;
      this.fetchData();
    },

    fetchData() {
      this.loading = true;
      const request = {
        page: this.options.page || 1,
        perPage: this.options.itemsPerPage || 10,
        sortBy: this.options.sortBy?.[0]?.key || null,
        sortDesc: this.options.sortBy?.[0]?.order === 'desc',
        category: this.filters.category,
        search: this.filters.search,
        isActive: this.filters.isActive,
      };

      this.$call.GetTableValidationMaster(
        request,
        (response) => {
          this.loading = false;
          if (response.status === 0) {
            this.items = response.data.data || [];
            this.totalItems = response.data.total || 0;
          } else {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to load data',
            });
          }
        },
        (error) => {
          this.loading = false;
          console.error('Error fetching data:', error);
        }
      );
    },

    fetchCategories() {
      this.$call.GetAllValidationCategories(
        (response) => {
          if (response.status === 0) {
            this.categories = response.data || [];
          }
        },
        (error) => {
          console.error('Error fetching categories:', error);
        }
      );
    },

    fetchCategoryStats() {
      this.$call.GetValidationCategoryStats(
        (response) => {
          if (response.status === 0) {
            this.categoryStats = response.data || [];
          }
        },
        (error) => {
          console.error('Error fetching category stats:', error);
        }
      );
    },

    // Form dialog methods
    openAddDialog() {
      this.isEditMode = false;
      this.resetForm();
      this.formDialog = true;
      this.onAnswerTypeChange(this.form.AnswerType);
    },

    openEditDialog(item) {
      this.isEditMode = true;

      // Calculate level count from existing data (use camelCase from API response)
      let levelCount = '1';
      if (item.expectedIntentL4) levelCount = '4';
      else if (item.expectedIntentL3) levelCount = '3';
      else if (item.expectedIntentL2) levelCount = '2';

      // V2: Determine validation flags
      const hasIntent = !!(item.expectedIntentL1 || item.expectedIntentL2 || item.expectedIntentL3 || item.expectedIntentL4);
      const hasAnswer = !!(item.expectedAnswer || item.answerGuide);

      this.form = {
        ID: item.id,
        Question: item.question,
        AnswerType: item.answerType || 'guide',
        QuestionType: item.questionType || '',
        ExpectedAnswer: item.expectedAnswer || '',
        Category: item.category,
        ValidateAnswer: hasAnswer || true, // V2: Set answer flag (default true if not found)
        ValidateIntent: hasIntent, // V2: Set intent flag
        levelCount: levelCount,
        ExpectedIntentL1: item.expectedIntentL1 || '',
        ExpectedIntentL2: item.expectedIntentL2 || '',
        ExpectedIntentL3: item.expectedIntentL3 || '',
        ExpectedIntentL4: item.expectedIntentL4 || '',
        ExpectedIntentFull: item.expectedIntentFull || '',
        AnswerGuide: item.answerGuide || '',
        Tags: item.tags || '',
        IsActive: item.isActive ?? true,
      };

      // Fetch child levels based on existing selections (only if has intent)
      if (hasIntent) {
        this.loadChildLevelsForEdit(item);
      }

      this.formDialog = true;
      this.onAnswerTypeChange(this.form.AnswerType);
    },

    // Helper method to load child levels when editing
    loadChildLevelsForEdit(item) {
      // Reset child options
      this.level2Options = [];
      this.level3Options = [];
      this.level4Options = [];
      this.selectedL1Id = null;
      this.selectedL2Id = null;
      this.selectedL3Id = null;

      // Find L1 ID and fetch L2 options
      if (item.expectedIntentL1) {
        const l1Item = this.level1Options.find(x => x.intentName === item.expectedIntentL1);
        if (l1Item) {
          this.selectedL1Id = l1Item.intentID;
          this.$call.GetValidationIntentLevel2(
            l1Item.intentID,
            (response) => {
              if (response.status === 0) {
                this.level2Options = response.data || [];
                // Continue loading L3 if L2 is selected
                if (item.expectedIntentL2) {
                  const l2Item = this.level2Options.find(x => x.intentName === item.expectedIntentL2);
                  if (l2Item) {
                    this.selectedL2Id = l2Item.intentID;
                    this.$call.GetValidationIntentLevel3(
                      l2Item.intentID,
                      (resp3) => {
                        if (resp3.status === 0) {
                          this.level3Options = resp3.data || [];
                          // Continue loading L4 if L3 is selected
                          if (item.expectedIntentL3) {
                            const l3Item = this.level3Options.find(x => x.intentName === item.expectedIntentL3);
                            if (l3Item) {
                              this.selectedL3Id = l3Item.intentID;
                              this.$call.GetValidationIntentLevel4(
                                l3Item.intentID,
                                (resp4) => {
                                  if (resp4.status === 0) {
                                    this.level4Options = resp4.data || [];
                                  }
                                },
                                (err) => console.error('Error loading L4:', err)
                              );
                            }
                          }
                        }
                      },
                      (err) => console.error('Error loading L3:', err)
                    );
                  }
                }
              }
            },
            (err) => console.error('Error loading L2:', err)
          );
        }
      }
    },

    closeFormDialog() {
      this.resetForm();
      if (this.$refs.formRef) {
        this.$refs.formRef.resetValidation();
      }
      this.formDialog = false;
    },

    resetForm() {
      this.form = {
        ID: null,
        Question: '',
        AnswerType: 'guide',
        QuestionType: '',
        ExpectedAnswer: '',
        Category: '',
        ValidateAnswer: true, // V2: Default checked
        ValidateIntent: false, // V2: Reset intent flag
        levelCount: '4', // Reset to 4 levels default
        ExpectedIntentL1: '',
        ExpectedIntentL2: '',
        ExpectedIntentL3: '',
        ExpectedIntentL4: '',
        ExpectedIntentFull: '',
        AnswerGuide: '',
        Tags: '',
        IsActive: true,
      };
      // Reset level options
      this.level2Options = [];
      this.level3Options = [];
      this.level4Options = [];
      this.selectedL1Id = null;
      this.selectedL2Id = null;
      this.selectedL3Id = null;
      if (this.$refs.formRef) {
        this.$refs.formRef.resetValidation();
      }
    },

    saveForm() {
      if (!this.formValid) return;

      // V2: Answer Quality is always required (ValidateAnswer is always true)
      // Only validate intent levels if intent validation is enabled
      if (this.form.ValidateIntent && !this.form.ExpectedIntentL1) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Intent Level Required',
          text: 'Please select at least Level 1 (L1) for intent validation',
        });
        return;
      }

      this.saving = true;
      const request = { ...this.form };

      if (this.isEditMode) {
        this.$call.UpdateValidationMaster(
          request,
          (response) => {
            this.saving = false;
            if (response.status === 0) {
              this.$swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Question updated successfully',
                timer: 1500,
                showConfirmButton: false,
              });
              this.closeFormDialog();
              this.fetchData();
              this.fetchCategoryStats();
            } else if (response.status === 3) {
              // Duplicate
              this.$swal.fire({
                icon: 'warning',
                title: 'Duplicate',
                text: response.message || 'Question ID already exists',
              });
            } else {
              this.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: response.message || 'Failed to save',
              });
            }
          },
          (error) => {
            this.saving = false;
            console.error('Error saving:', error);
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'An error occurred while saving',
            });
          }
        );
      } else {
        this.$call.CreateValidationMaster(
          request,
          (response) => {
            this.saving = false;
            if (response.status === 0) {
              this.$swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Question created successfully',
                timer: 1500,
                showConfirmButton: false,
              });
              this.closeFormDialog();
              this.fetchData();
              this.fetchCategoryStats();
            } else if (response.status === 3) {
              // Duplicate
              this.$swal.fire({
                icon: 'warning',
                title: 'Duplicate',
                text: response.message || 'Question ID already exists',
              });
            } else {
              this.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: response.message || 'Failed to save',
              });
            }
          },
          (error) => {
            this.saving = false;
            console.error('Error saving:', error);
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'An error occurred while saving',
            });
          }
        );
      }
    },

    confirmDelete(item) {
      this.$swal
        .fire({
          title: 'Delete Question?',
          text: `Are you sure you want to delete this question?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteItem(item.id);
          }
        });
    },

    deleteItem(id) {
      this.loading = true;
      this.$call.DeleteValidationMaster(
        id,
        (response) => {
          this.loading = false;
          if (response.status === 0) {
            this.$swal.fire({
              icon: 'success',
              title: 'Deleted',
              text: 'Question deleted successfully',
              timer: 1500,
              showConfirmButton: false,
            });
            this.fetchData();
            this.fetchCategoryStats();
          } else {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to delete',
            });
          }
        },
        (error) => {
          this.loading = false;
          console.error('Error deleting:', error);
        }
      );
    },

    // Category dialog methods
    openCategoryDialog() {
      this.fetchCategories();
      this.showCategoryForm = false;
      this.categoryDialog = true;
    },

    openAddCategoryForm() {
      this.isEditCategoryMode = false;
      this.categoryForm = {
        id: null,
        categoryName: '',
        description: '',
        color: '#607D8B',
        sortOrder: 0,
        isActive: true,
      };
      this.showCategoryForm = true;
    },

    editCategory(cat) {
      this.isEditCategoryMode = true;
      this.categoryForm = {
        id: cat.id,
        categoryName: cat.categoryName,
        description: cat.description || '',
        color: cat.color || '#607D8B',
        sortOrder: cat.sortOrder || 0,
        isActive: cat.isActive ?? true,
      };
      this.showCategoryForm = true;
    },

    cancelCategoryForm() {
      this.showCategoryForm = false;
    },

    saveCategory() {
      if (!this.categoryForm.categoryName) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Validation',
          text: 'Category name is required',
        });
        return;
      }

      this.savingCategory = true;
      const request = { ...this.categoryForm };

      if (this.isEditCategoryMode) {
        this.$call.UpdateIntentCategory(
          request,
          (response) => {
            this.savingCategory = false;
            if (response.status === 0) {
              this.$swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Category updated',
                timer: 1500,
                showConfirmButton: false,
              });
              this.showCategoryForm = false;
              this.fetchCategories();
              this.fetchCategoryStats();
            } else if (response.status === 3) {
              this.$swal.fire({
                icon: 'warning',
                title: 'Duplicate',
                text: response.message || 'Category name already exists',
              });
            } else {
              this.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: response.message || 'Failed to save category',
              });
            }
          },
          (error) => {
            this.savingCategory = false;
            console.error('Error saving category:', error);
          }
        );
      } else {
        this.$call.CreateIntentCategory(
          request,
          (response) => {
            this.savingCategory = false;
            if (response.status === 0) {
              this.$swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Category created',
                timer: 1500,
                showConfirmButton: false,
              });
              this.showCategoryForm = false;
              this.fetchCategories();
              this.fetchCategoryStats();
            } else if (response.status === 3) {
              this.$swal.fire({
                icon: 'warning',
                title: 'Duplicate',
                text: response.message || 'Category name already exists',
              });
            } else {
              this.$swal.fire({
                icon: 'error',
                title: 'Error',
                text: response.message || 'Failed to save category',
              });
            }
          },
          (error) => {
            this.savingCategory = false;
            console.error('Error saving category:', error);
          }
        );
      }
    },

    confirmDeleteCategory(cat) {
      if (cat.questionCount > 0) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Cannot Delete',
          text: `This category has ${cat.questionCount} questions. Remove or reassign them first.`,
        });
        return;
      }

      this.$swal
        .fire({
          title: 'Delete Category?',
          text: `Are you sure you want to delete "${cat.categoryName}"?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Yes, delete it',
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteCategory(cat.id);
          }
        });
    },

    deleteCategory(id) {
      this.$call.DeleteIntentCategory(
        id,
        (response) => {
          if (response.status === 0) {
            this.$swal.fire({
              icon: 'success',
              title: 'Deleted',
              text: 'Category deleted successfully',
              timer: 1500,
              showConfirmButton: false,
            });
            this.fetchCategories();
            this.fetchCategoryStats();
          } else {
            this.$swal.fire({
              icon: 'error',
              title: 'Error',
              text: response.message || 'Failed to delete category',
            });
          }
        },
        (error) => {
          console.error('Error deleting category:', error);
        }
      );
    },

    // Helper methods
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    getCategoryColor(categoryName) {
      const cat = this.categories.find((c) => c.categoryName === categoryName);
      return cat?.color || '#607D8B';
    },

    getAnswerTypeText(value) {
      const option = this.answerTypeOptions.find((o) => o.value === value);
      return option?.text || value || '-';
    },
  },
};
</script>

<style scoped>
.v-data-table {
  font-size: 14px;
}

/* Tooltip help icon */
.cursor-pointer {
  cursor: pointer;
}

/* Intent Path Field Styling */
.intent-path-field :deep(.v-field__input) {
  color: #2e7d32 !important;
  font-weight: 500;
  font-family: 'Courier New', monospace;
}

.intent-path-field :deep(.v-field--disabled) {
  opacity: 0.9 !important;
}

/* Level Chip Selection */
:deep(.v-chip-group .v-chip) {
  font-weight: 500;
}

:deep(.v-chip-group .v-chip--selected) {
  border: 2px solid #1976d2;
}

/* Custom Switch Styling */
.custom-switch {
  margin-left: 0;
}

.custom-switch :deep(.v-switch__track) {
  height: 28px;
  width: 48px;
  opacity: 1;
}

.custom-switch :deep(.v-switch__thumb) {
  width: 20px;
  height: 20px;
}
</style>
